import { toDisapproval, type DisapprovalOptions, type DisapprovalValues } from '@/shared/action/disapproval-fields';
import type { MultiOption } from '@/shared/action';
import { civilDateAt } from '@/shared/format/date';

import { toCancelBody, toDisapproveBody } from './credit-note.mappers';
import type { CreditNote, ListCancelBody } from './credit-note.wire';

// Os formulários de "reprovar com bloqueio" da operação → corpo do fio. Funções puras, para a tela e
// o agente (manifesto, import dinâmico) mandarem o mesmo corpo para os mesmos valores (C4).

/** A validade do bloqueio é data civil na tela; o legado manda o instante da meia-noite (yup → `Date`). */
export const civilToIso = (day: string | null | undefined) => (day ? new Date(`${day}T00:00:00-03:00`).toISOString() : null);
export const todayCivil = () => civilDateAt(new Date());

/** Rejeitar do detalhe (compliance, crédito, liquidação, vistoria): RejectForm.tsx. */
export const detailReject = (): DisapprovalOptions => ({ today: todayCivil() });
/** Rejeitar instrumento: só o texto (schema padrão do RejectForm). */
export const instrumentReject: DisapprovalOptions = { withReasons: false, withBlock: false, message: 'required' };
/** Cancelar: motivo, bloqueio com blacklist em qualquer bloqueio e texto sempre (CancelForm.tsx). */
export const cancelOptions = (): DisapprovalOptions => ({ today: todayCivil(), message: 'required', blacklist: 'anyBlock' });
/** Rejeitar assinatura (SignaturesValidation): motivos literais e texto com "Outros motivos". */
export const signatureReject: DisapprovalOptions = { withBlock: false, blacklist: false };

/** Corpo do `/disapprove` do detalhe a partir dos campos de tela (U20-4 pela chave de `shared/action`). */
export function detailDisapproveBody(values: DisapprovalValues, options: DisapprovalOptions) {
  const out = toDisapproval(values, options);
  return toDisapproveBody({
    message: out.message ?? null,
    insertBlock: out.block,
    blockUntil: civilToIso(out.blockUntil),
    complianceChecklist: out.blacklist ?? null,
    rejectionReasonCode: out.reasons ?? null,
  });
}

/** Corpo do `/cancel` do detalhe (CancelFormContainer.tsx:54-60): `complianceChecklist` vira `attributeTypes`. */
export function cancelBody(values: DisapprovalValues) {
  const out = toDisapproval(values, cancelOptions());
  return toCancelBody({
    message: out.message ?? '',
    insertBlock: out.block,
    blockUntil: out.block === null ? null : civilToIso(out.blockUntil),
    rejectionReasonCode: out.reasons ?? [],
    complianceChecklist: out.blacklist ?? null,
  });
}

/**
 * Corpo do `/cancel` do lote (C1): o legado manda o formulário cru (creditNoteListActions.tsx:220-224),
 * com os defaults de CancelSchema.ts:32-38 (`complianceChecklist: []`, `blockUntil: null`) e sem
 * `attributeTypes`. Os `*Display` do `SelectFormField` ficam fora, como no rejeitar em lote.
 */
export function listCancelBody(values: DisapprovalValues): ListCancelBody {
  const out = toDisapproval(values, cancelOptions());
  return {
    message: out.message ?? '',
    insertBlock: out.block,
    blockUntil: out.block === null ? null : civilToIso(out.blockUntil),
    rejectionReasonCode: out.reasons ?? [],
    complianceChecklist: out.blacklist ?? [],
  };
}

/** Blacklist a partir do que a operação traz do tomador (CPF/CNPJ e e-mail; `blackListOptions`, proposta 3 do E8b). */
export function blacklistOf(note: Pick<CreditNote, 'personRegistrationNumber' | 'personDiscriminator' | 'personEmail'> | undefined, labels: { readonly cpf: string; readonly cnpj: string; readonly email: string }): MultiOption[] {
  if (!note) return [];
  const options: MultiOption[] = [];
  if (note.personRegistrationNumber) options.push({ value: 'registrationnumber', label: note.personDiscriminator === 'LegalPerson' ? labels.cnpj : labels.cpf });
  if (note.personEmail) options.push({ value: 'email', label: labels.email });
  return options;
}
